import React, { useState, useEffect } from 'react';
import { useBatteryStore } from '../../store/batteryStore';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle,
  DialogTrigger 
} from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { 
  Save, 
  FolderOpen, 
  Trash2, 
  FileText,
  Clock
} from 'lucide-react';

export const DraftManager = () => {
  const { 
    drafts, 
    currentDraftId,
    loadDrafts, 
    saveDraft, 
    loadDraft, 
    deleteDraft,
    selectedResult 
  } = useBatteryStore();
  
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [draftName, setDraftName] = useState('');
  
  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);
  
  const handleSave = () => {
    const draft = saveDraft(draftName || `Draft ${new Date().toLocaleDateString()}`);
    setDraftName('');
    setSaveDialogOpen(false);
  };
  
  const handleLoad = (draftId) => {
    loadDraft(draftId);
    setLoadDialogOpen(false);
  };
  
  const handleDelete = (e, draftId) => {
    e.stopPropagation();
    deleteDraft(draftId);
  };
  
  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleString();
  };
  
  return (
    <div className="flex gap-2" data-testid="draft-manager">
      {/* Save Draft Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            disabled={!selectedResult}
            className="text-xs"
            data-testid="save-draft-btn"
          >
            <Save className="w-3 h-3 mr-1" />
            Save Draft
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="font-heading">Save Configuration</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Save your current pack configuration for later use.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Enter draft name..."
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              className="bg-zinc-800 border-zinc-700"
              data-testid="draft-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-save-btn">
              Save Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Load Draft Dialog */}
      <Dialog open={loadDialogOpen} onOpenChange={setLoadDialogOpen}>
        <DialogTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            className="text-xs"
            data-testid="load-draft-btn"
          >
            <FolderOpen className="w-3 h-3 mr-1" />
            Load Draft
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Load Configuration</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Select a saved configuration to load.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-64 mt-4">
            {drafts.length === 0 ? (
              <div className="text-center text-zinc-500 py-8">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <div className="text-sm">No saved drafts</div>
              </div>
            ) : (
              <div className="space-y-2">
                {drafts.map((draft) => (
                  <button
                    key={draft.id}
                    onClick={() => handleLoad(draft.id)}
                    className={`w-full p-3 rounded-lg border text-left transition-colors hover:bg-zinc-800 ${
                      currentDraftId === draft.id 
                        ? 'border-blue-500 bg-blue-500/10' 
                        : 'border-zinc-700 bg-zinc-800/50'
                    }`}
                    data-testid={`draft-item-${draft.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-medium text-zinc-100 text-sm">
                          {draft.name}
                        </div>
                        <div className="text-xs text-zinc-500 flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(draft.createdAt)}
                        </div>
                        <div className="text-xs text-cyan-400 font-mono mt-1">
                          {draft.config?.series}S{draft.config?.parallel}P • {draft.selectedCellId}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-zinc-500 hover:text-red-400"
                        onClick={(e) => handleDelete(e, draft.id)}
                        data-testid={`delete-draft-${draft.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};
